import { useCallback, useMemo, useRef } from 'react';
import {
  useLazyIndicatorConversations,
  type IndicatorItem,
  type IndicatorSelectHandler,
  type QualitySummaryParams,
} from '@/entities/dashboards';
import {
  CONVERSATION_ROUTE_PATH,
  CONVERSATION_SELECTION_QUERY_PARAM,
  CONVERSATION_SELECTION_SOURCES,
  type ConversationSelection,
  type ConversationSelectionSource,
  stashConversationSelection,
  useConversationLink,
} from '@/entities/conversations';
import { appGeneratePath, openUrlInNewTab, useAppNavigate } from '@/shared/lib';

type Params = {
  /** Фильтры, с которыми получена сводка: с ними же собирается состав показателя на переходе */
  summaryParams: QualitySummaryParams;
};

type Result = {
  /** Обработчик выбора для каждого источника выборки: карточка сообщает элемент, страница открывает список */
  selectHandlers: Record<ConversationSelectionSource, IndicatorSelectHandler>;
  /** Открывает один диалог по идентификатору разговора */
  openConversation: (conversationId: number, openInNewTab: boolean) => void;
};

/**
 * Переходы дашборда к диалогам: к выборке показателя и к одному диалогу.
 *
 * Показателей на дашборде много, а различаются переходы только составом, описанием и признаком
 * источника, поэтому переход собирается одним способом на все карточки. Вкладку выбирает
 * пользователь: обычный клик ведёт в текущей, shift+клик открывает новую. Открытие идёт мимо
 * `window.open`: под shift браузер понимает его как «отдельное окно» — см. {@link openUrlInNewTab}.
 *
 * Выборка адреса не имеет: состав — сотни, а у критерия и тысячи идентификаторов. Поэтому в новую
 * вкладку она передаётся через хранилище браузера, а в адрес идёт лишь ключ к ней.
 */
export const useIndicatorTransitions = ({ summaryParams }: Params): Result => {
  const navigate = useAppNavigate();
  const buildConversationLink = useConversationLink();
  const { refetch: fetchIndicatorConversations } = useLazyIndicatorConversations();

  // Пока состав показателя грузится, повторный клик по нему второго запроса не начинает:
  // ref, а не состояние, — признак нужен самому обработчику, а не разметке.
  const isTransitionPending = useRef(false);

  const openSelection = useCallback(
    (selection: ConversationSelection, openInNewTab: boolean) => {
      if (openInNewTab) {
        const stashKey = stashConversationSelection(selection);
        const path = appGeneratePath('/dialogs/:id?', { id: null });
        openUrlInNewTab(`${path}?${CONVERSATION_SELECTION_QUERY_PARAM}=${stashKey}`);
        return;
      }
      navigate('/dialogs/:id?', { id: null }, { state: selection });
    },
    [navigate],
  );

  const openConversation = useCallback(
    (conversationId: number, openInNewTab: boolean) => {
      const { to, state } = buildConversationLink(conversationId);
      if (openInNewTab) {
        openUrlInNewTab(to);
        return;
      }
      // Источник перехода — текущий интерфейс (дашборд): «Назад» в карточке диалога
      // вернёт сюда, а не в общий список диалогов.
      navigate(CONVERSATION_ROUTE_PATH, { id: String(conversationId) }, { state });
    },
    [navigate, buildConversationLink],
  );

  // Состав показателя сводки приходит на клике: сводка несёт лишь отметку, по которой сервер
  // соберёт диалоги теми же фильтрами, какими посчитан сам показатель. Отказ запроса перехода
  // не выполняет — пользователь остаётся на дашборде, а не попадает в список с пустым отбором.
  const openBySelectionMark = useCallback(
    async (mark: string, item: IndicatorItem, source: ConversationSelectionSource, openInNewTab: boolean) => {
      if (isTransitionPending.current) return;
      isTransitionPending.current = true;
      try {
        const { conversationIds } = await fetchIndicatorConversations({ ...summaryParams, selection: mark });
        if (conversationIds.length === 0) return;
        openSelection({ conversationIds, description: item.label, source }, openInNewTab);
      } catch {
        // Отказ показан общим уведомлением клиента: перехода нет, дашборд остаётся на месте.
      } finally {
        isTransitionPending.current = false;
      }
    },
    [fetchIndicatorConversations, summaryParams, openSelection],
  );

  // Показатель без диалогов не кликабелен, но проверка источника живёт и здесь: пустой состав
  // открыл бы список с фильтром, не находящим ничего.
  const createSelectHandler = useCallback(
    (source: ConversationSelectionSource): IndicatorSelectHandler =>
      (item, openInNewTab) => {
        if (item.conversationIds?.length) {
          openSelection({ conversationIds: item.conversationIds, description: item.label, source }, openInNewTab);
          return;
        }
        // Вкладку выбирает клик: признак shift известен сейчас и до открытия списка обязан дожить.
        if (item.selection) void openBySelectionMark(item.selection, item, source, openInNewTab);
      },
    [openSelection, openBySelectionMark],
  );

  const selectHandlers = useMemo(
    () =>
      Object.fromEntries(
        CONVERSATION_SELECTION_SOURCES.map((source) => [source, createSelectHandler(source)]),
      ) as Record<ConversationSelectionSource, IndicatorSelectHandler>,
    [createSelectHandler],
  );

  return { selectHandlers, openConversation };
};
